import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subscription, take } from "rxjs";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import dayjs from "dayjs/esm";
import customParseFormat from "dayjs/esm/plugin/customParseFormat";
import { Bill } from "src/app/shared/types/contract-types";
import { EInvoice, ReverseChargeReason, SendToCirOption } from "src/app/shared/types/e-invoice";
import { TaxRateCategory } from "src/app/shared/types/tax-rate-categories";
import { EInvoiceService } from "src/app/shared/services/e-invoice.service";
import { TaxRateCategoriesService } from "src/app/shared/services/tax-rate-categories.service";
import { showMessage } from "src/app/shared/utils/toast.popup";
import { PopupType } from "src/app/shared/enums/popup-types";
import { ExchangeRateService } from "src/app/shared/services/exchange-rates.service";
import { NumberFormatService } from "src/app/shared/services/number-format.service";
import { DateTimeFormatter } from "src/app/shared/utils/datetime.formatter";

dayjs.extend(customParseFormat);

@Component({
  selector: "app-e-invoice",
  templateUrl: "./e-invoice.component.html",
  standalone: false,
})
export class EInvoiceComponent implements OnInit, OnDestroy {
  @Input() bill!: Bill;
  isLoading = false;
  SendToCirOption = SendToCirOption;
  sendToCirOption: SendToCirOption = SendToCirOption.Yes;
  @Output() updated: EventEmitter<void> = new EventEmitter();
  appCurrency = "RSD";
  public taxCategoryOptions$!: Observable<TaxRateCategory[]>;
  private categorySubscription: Subscription | undefined;
  private typeSubscription: Subscription | undefined;
  public form!: FormGroup;
  taxRate = 0;
  reverseChargeReasons: ReverseChargeReason[] = [];
  categories: string[] = [];
  filteredReasons: ReverseChargeReason[] = [];
  selectedCategory = "";
  showTaxForm = false;

  descriptionCodeOptions = [
    { label: "Datum prometa", value: 35 },
    { label: "Član 16. tačka 2a) ZPDV", value: 3 },
    { label: "Član 36a ZPDV", value: 432 },
  ];

  constructor(
    private eInvoiceService: EInvoiceService,
    public translate: TranslateService,
    public taxRateService: TaxRateCategoriesService,
    public fb: FormBuilder,
    public exchangeService: ExchangeRateService,
    public numberFormatService: NumberFormatService,
  ) {}

  ngOnInit(): void {
    this.fetchExchangeRate();
    this.getReverseChargeReasons();
  }

  fetchExchangeRate() {
    this.exchangeService.getExchangeRateBetweeCurrencies(this.bill.currencyCode, "RSD").pipe(take(1)).subscribe((rate) => {
      this.initForm(rate);
    });
  }

  options = [
    { value: true, label: this.translate.instant("e-invoice.final_invoice") },
    { value: false, label: this.translate.instant("e-invoice.advance_invoice") },
  ];

  initForm(exchangeRate: number) {
    const { price } = this.bill;
    const initialAmount = Number((price * exchangeRate).toFixed(2));
    const today = dayjs().format("DD.MM.YYYY");
    this.form = this.fb.group({
      type: [{ value: this.options[0], disabled: true }, Validators.required],
      cashier: [`TuristAgent`, Validators.required],
      amount: [initialAmount, Validators.required],
      taxCategory: [null, Validators.required],
      initialAmount: [price],
      sendToCirOption: [this.sendToCirOption],
      taxExemptionReasonCode: [null],
      taxExemptionReason: [null],
      taxLabel: [{ value: null, disabled: true }],
      contractNumber: [`${this.bill.id}/${new Date().getFullYear()}`, Validators.required],
      actualDeliveryDate: [today, Validators.required],
      dueDate: [today],
      descriptionCode: [this.descriptionCodeOptions[0].value],
    });
    this.taxCategoryOptions$ = this.taxRateService.getTaxRateCategories();
    this.categorySubscription = this.form.get("taxCategory")?.valueChanges.subscribe((category) => {
      if (category) {
        setTimeout(() => {
          this.setReasonsByCategory(category);
          this.taxRate = category.percentage;
          this.form.get("taxExemptionReasonCode")?.setValue(category.code);
          this.form.get("taxExemptionReason")?.setValue(category.desicionNumber);
          this.form.get("taxLabel")?.setValue(category.taxLabel);
        }, 1000);
      }
      else {
        this.taxRate = 0;
        this.form.get("taxExemptionReasonCode")?.reset();
        this.form.get("taxExemptionReason")?.reset();
      }
    });
    this.typeSubscription = this.form.get("type")?.valueChanges.subscribe(type => this.setAmout(type.value, this.bill));

    this.form.get("descriptionCode")?.valueChanges.subscribe((value) => {
      const dueControl = this.form.get("dueDate");

      if (value === 432) {
        dueControl?.setValidators([Validators.required]);
      }
      else {
        dueControl?.clearValidators();
      }

      dueControl?.updateValueAndValidity();
    });
  }

  get taxLabelTranslationKey(): string {
    const taxLabel = this.form?.get("taxLabel")?.value;
    if (taxLabel && taxLabel.startsWith("S")) {
      return "e-invoice_sales.tax_categories.vat_applied";
    }
    return "e-invoice_sales.tax_categories.add_base_exemption";
  }

  ngOnDestroy(): void {
    if (this.categorySubscription) {
      this.categorySubscription.unsubscribe();
    }
    if (this.typeSubscription) {
      this.typeSubscription.unsubscribe();
    }
  }

  setAmout(type: boolean, bill: Bill): void {
    const amount = bill.price ?? 1;
    if (this.appCurrency === "RSD") {
      this.form.get("amount")?.setValue(Number((amount * (bill.exchangeRate ?? 1)).toFixed(2)));
    }
    else {
      const rateSubscription = this.exchangeService.getExchangeRateBetweeCurrencies(this.bill.currencyCode, "RSD").subscribe((rate) => {
        rateSubscription.unsubscribe();
        this.form.get("amount")?.setValue(Number((amount * rate).toFixed(2)));
      });
    }
    this.form.get("initialAmount")?.setValue(amount);
  }

  sendToEInvoiceSystem(): void {
    this.isLoading = true;
    const { cashier, amount, taxCategory, initialAmount, taxExemptionReasonCode, taxExemptionReason, contractNumber, descriptionCode, actualDeliveryDate, dueDate } = this.form.value;
    const newInvoice: EInvoice = {
      invoiceId: this.bill.id,
      requestId: this.bill.id.toString(),
      sendToCir: this.form.value.sendToCirOption,
      contractNumber,
      type: "FINAL",
      executeValidation: true,
      actualDeliveryDate: DateTimeFormatter.safeParseSimpleDate(actualDeliveryDate) ?? dayjs().format("YYYY-MM-DD"),
      dueDate: DateTimeFormatter.safeParseSimpleDate(dueDate),
      ...{ cashier, amount, taxCategory, initialAmount, taxExemptionReasonCode, taxExemptionReason },
      ...(this.isSCategory ? { descriptionCode } : {}),
    };
    const subscription = this.eInvoiceService.createUBLInvoice(newInvoice).subscribe({
      next: () => {
        this.isLoading = false;
        subscription.unsubscribe();
        this.updated.emit();
        showMessage(PopupType.Success, this.translate.instant("e-invoice.e_invoice_sent_successfully"));
      },
      error: (error) => {
        subscription.unsubscribe();
        showMessage(PopupType.Danger, error.error);
        this.isLoading = false;
      },
    });
  }

  checkCompanyBeforeSending(): void {
    if (!this.bill?.id) {
      showMessage(PopupType.Warning, this.translate.instant("e-invoice.missing_invoice_number"));
      return;
    }

    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const subscription = this.eInvoiceService.checkIfCompanyRegistered(this.bill.id).subscribe({
      next: (response) => {
        subscription.unsubscribe();
        this.isLoading = false;

        if (!response || !response.eFakturaRegisteredCompany) {
          showMessage(PopupType.Danger, this.translate.instant("e-invoice.company_not_registered"));
          return;
        }
        this.sendToEInvoiceSystem();
      },
      error: () => {
        subscription.unsubscribe();
        this.isLoading = false;
      },
    });
  }

  public formatValue = (value: string | number | undefined | null): string => {
    return this.numberFormatService.toFixed(value);
  };

  getReverseChargeReasons(): void {
    this.isLoading = true;
    this.eInvoiceService.getReverseChargeReasons().subscribe({
      next: (reasons: ReverseChargeReason[]) => {
        this.reverseChargeReasons = reasons;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  setReasonsByCategory(selectedCategory: { taxLabel: string }): void {
    this.filteredReasons = [];
    this.selectedCategory = selectedCategory.taxLabel.trim();
    this.filteredReasons = this.reverseChargeReasons.filter(reason => reason.category === this.selectedCategory);
  }

  get isSCategory(): boolean {
    const taxLabel = this.form?.get("taxLabel")?.value;
    return taxLabel === "S";
  }
}
